import * as Yup from 'yup'
import { EMAIL_REG, NAME_REG, SIX_DIGIT_OTP } from 'app/Bookings/constants'
import { isDateValid, getErrorMessage, ErrorMessage } from 'utils/common'
import { dateFormats } from 'constants/common'
import startOfToday from 'date-fns/startOfToday'
import parse from 'date-fns/parse'
import subDays from 'date-fns/subDays'
import format from 'date-fns/format'
import subYears from 'date-fns/subYears'

export function getFirstNameSchema(
  isSupportOrThirdPartyUser?: boolean,
  customErrorMessage?: string
): Yup.StringSchema {
  return Yup.string()
    .required(
      customErrorMessage ??
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'first name',
          isSupportOrThirdPartyUser
        )
    )
    .max(255, getErrorMessage(ErrorMessage.MAX, 'first name'))
    .test(
      'empty_check',
      getErrorMessage(ErrorMessage.NOT_VALID, 'first name'),
      (fName) => fName?.trim().length !== 0
    )
    .matches(NAME_REG, getErrorMessage(ErrorMessage.NOT_VALID, 'first name'))
}

export function getLastNameSchema(
  isSupportOrThirdPartyUser?: boolean,
  customErrorMessage?: string
): Yup.StringSchema {
  return Yup.string()
    .required(
      customErrorMessage ??
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'last name',
          isSupportOrThirdPartyUser
        )
    )
    .max(255, getErrorMessage(ErrorMessage.MAX, 'last name'))
    .matches(NAME_REG, getErrorMessage(ErrorMessage.NOT_VALID, 'last name'))
    .test(
      'empty_check',
      getErrorMessage(ErrorMessage.NOT_VALID, 'last name'),
      (lName) => lName?.trim().length !== 0
    )
}

export function getCustomEmailValidationSchema(
  customErrorMessage?: string
): Yup.StringSchema {
  return Yup.string()
    .matches(
      EMAIL_REG,
      customErrorMessage ??
        getErrorMessage(ErrorMessage.NOT_VALID, 'email address.')
    )
    .max(255, getErrorMessage(ErrorMessage.MAX, 'email address.'))
    .test(
      'empty_check',
      customErrorMessage ??
        getErrorMessage(ErrorMessage.NOT_VALID, 'email address.'),
      (email) => email?.trim().length !== 0
    )
}

export const getDateOfBirthSchema = (
  isSupportOrThirdPartyUser?: boolean
): Yup.StringSchema => {
  return Yup.string()
    .required(
      getErrorMessage(
        ErrorMessage.REQUIRED,
        'date of birth',
        isSupportOrThirdPartyUser
      )
    )
    .test('valid_check', 'Please enter a valid date', (value) =>
      isDateValid(value)
    )
    .test(
      'max_check',
      'Date cannot be greater than or equal to current date',
      (value) =>
        startOfToday() >
        parse(value, dateFormats.YEAR_MONTH_DAY_FORMAT, new Date())
    )
    .test(
      'min_check',
      `Please choose a birthdate after ${format(
        subDays(subYears(new Date(), 110), 1),
        dateFormats.DAY_MONTHNAME_YEAR_FORMAT
      )}`,
      (value) =>
        subYears(new Date(), 110).setHours(0, 0, 0, 0) <=
        new Date(value).setHours(0, 0, 0, 0)
    )
}

export const getDateOfBirthSchemaNoMinor = (
  isSupportOrThirdPartyUser?: boolean
): Yup.StringSchema => {
  return getDateOfBirthSchema(isSupportOrThirdPartyUser).test(
    'min_check',
    'Cannot be a minor',
    (value) =>
      subYears(new Date(), 18).setHours(0, 0, 0, 0) >
      new Date(value).setHours(0, 0, 0, 0)
  )
}

export const genderSchema = Yup.string().required('Please select a gender')

export function getAddressSchema(
  isSupportOrThirdPartyUser?: boolean
): Yup.AnyObjectSchema {
  return Yup.object().shape({
    line1: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'address',
          isSupportOrThirdPartyUser
        )
      )
      .nullable(),
    line2: Yup.string().nullable(),
    city: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'city',
          isSupportOrThirdPartyUser
        )
      )
      .nullable(),
    state: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'state',
          isSupportOrThirdPartyUser
        )
      )
      .length(2, 'Please choose a valid State from the dropdown')
      .nullable(),
    country: Yup.string().nullable(),
    zipcode: Yup.string()
      .required(
        getErrorMessage(
          ErrorMessage.REQUIRED,
          'zip code',
          isSupportOrThirdPartyUser
        )
      )
      .min(5, getErrorMessage(ErrorMessage.NOT_VALID, '5-digit zip code'))
      .length(5, getErrorMessage(ErrorMessage.NOT_VALID, '5-digit zip code'))
      .nullable(),
  })
}

export const emailValidationSchema = Yup.string()
  .matches(EMAIL_REG, getErrorMessage(ErrorMessage.NOT_VALID, 'email address.'))
  .required(getErrorMessage(ErrorMessage.REQUIRED, 'email address.'))
  .max(255, getErrorMessage(ErrorMessage.MAX, 'email address.'))
  .test(
    'empty_check',
    getErrorMessage(ErrorMessage.NOT_VALID, 'email address.'),
    (fName) => fName?.trim().length !== 0
  )

export const emailSchema = Yup.object().shape({
  email: emailValidationSchema,
})

export const otpSchema = Yup.object().shape({
  otp: Yup.string()
    .required('Please enter the OTP')
    .matches(SIX_DIGIT_OTP, 'OTP must be exactly 6 digits'),
})
